import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Video, MapPin, Send, Check } from 'lucide-react';
import { TextInput, TextArea } from '../FormElements';
interface MeetingSchedulerProps {
  isOpen: boolean;
  onClose: () => void;
  role: 'brand' | 'organizer';
  recipientName?: string;
}
export function MeetingScheduler({
  isOpen,
  onClose,
  role,
  recipientName = 'Partner'
}: MeetingSchedulerProps) {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: '30',
    type: 'video',
    location: '',
    notes: ''
  });
  const [sent, setSent] = useState(false);
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In real app, send meeting invite
    setSent(true);
    setTimeout(() => {
      setSent(false);
      onClose();
      setFormData({
        title: '',
        date: '',
        time: '',
        duration: '30',
        type: 'video',
        location: '',
        notes: ''
      });
    }, 2000);
  };
  return <AnimatePresence data-id="element-237">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-238">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} onClick={e => e.stopPropagation()} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden" data-id="element-239">
            {sent ? <div className="p-12 text-center" data-id="element-240">
                <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${role === 'brand' ? 'bg-cyan-500/20' : 'bg-purple-500/20'}`} data-id="element-241">
                  <Check size={40} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-242" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-243">
                  Invite Sent!
                </h3>
                <p className="text-slate-400" data-id="element-244">
                  {recipientName} will receive your meeting invitation.
                </p>
              </div> : <>
                <div className="p-6 border-b border-slate-800 flex items-center justify-between" data-id="element-245">
                  <div className="flex items-center gap-3" data-id="element-246">
                    <div className={`p-2 rounded-lg ${role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`} data-id="element-247">
                      <Calendar size={20} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-248" />
                    </div>
                    <div data-id="element-249">
                      <h2 className="text-lg font-bold text-white" data-id="element-250">
                        Schedule Meeting
                      </h2>
                      <p className="text-sm text-slate-400" data-id="element-251">
                        with {recipientName}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-252">
                    <X size={20} data-id="element-253" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-254">
                  <TextInput label="Meeting Title" placeholder="e.g. Sponsorship Discussion" value={formData.title} onChange={e => setFormData({
              ...formData,
              title: e.target.value
            })} required data-id="element-255" />

                  <div className="grid grid-cols-2 gap-4" data-id="element-256">
                    <div data-id="element-257">
                      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-258">
                        Date
                      </label>
                      <input type="date" value={formData.date} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-slate-600" required data-id="element-259" />
                    </div>
                    <div data-id="element-260">
                      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-261">
                        Time
                      </label>
                      <input type="time" value={formData.time} onChange={e => setFormData({
                  ...formData,
                  time: e.target.value
                })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-slate-600" required data-id="element-262" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-263">
                    <div data-id="element-264">
                      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-265">
                        Duration
                      </label>
                      <select value={formData.duration} onChange={e => setFormData({
                  ...formData,
                  duration: e.target.value
                })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-slate-600" data-id="element-266">
                        <option value="15" data-id="element-267">15 minutes</option>
                        <option value="30" data-id="element-268">30 minutes</option>
                        <option value="45" data-id="element-269">45 minutes</option>
                        <option value="60" data-id="element-270">1 hour</option>
                        <option value="90" data-id="element-271">1.5 hours</option>
                      </select>
                    </div>
                    <div data-id="element-272">
                      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-273">
                        Type
                      </label>
                      <div className="flex gap-2" data-id="element-274">
                        <button type="button" onClick={() => setFormData({
                    ...formData,
                    type: 'video'
                  })} className={`flex-1 py-3 rounded-lg border flex items-center justify-center gap-2 transition-colors ${formData.type === 'video' ? role === 'brand' ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' : 'border-purple-500 bg-purple-500/10 text-purple-400' : 'border-slate-700 text-slate-400 hover:border-slate-600'}`} data-id="element-275">
                          <Video size={18} data-id="element-276" /> Video
                        </button>
                        <button type="button" onClick={() => setFormData({
                    ...formData,
                    type: 'in-person'
                  })} className={`flex-1 py-3 rounded-lg border flex items-center justify-center gap-2 transition-colors ${formData.type === 'in-person' ? role === 'brand' ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' : 'border-purple-500 bg-purple-500/10 text-purple-400' : 'border-slate-700 text-slate-400 hover:border-slate-600'}`} data-id="element-277">
                          <MapPin size={18} data-id="element-278" /> In-person
                        </button>
                      </div>
                    </div>
                  </div>

                  {formData.type === 'in-person' && <TextInput label="Location" placeholder="e.g. Coffee Shop, Office Address" value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} data-id="element-279" />}

                  <TextArea label="Notes (Optional)" placeholder="Add any details or agenda items..." value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} data-id="element-280" />

                  <button type="submit" className={`w-full py-4 rounded-full font-bold transition-all flex items-center justify-center gap-2 ${buttonColor}`} data-id="element-281">
                    <Send size={18} data-id="element-282" /> Send Meeting Invite
                  </button>
                </form>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}